import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import type { FlowStep } from "../core/contracts";
import { PALETTE } from "./palette";
import { OptimizerInterior } from "./optimizer-interior";
import {
  DEFAULT_EXHIBIT_OPTIONS,
  interiorProgress,
  oreExhibit,
  shouldEnterInterior,
  type ExhibitOptions,
  type OreExhibit,
} from "./ore-exhibit";

export interface InteriorState {
  available: boolean;
  active: boolean;
  room: string;
  boundary: string;
}

interface DistrictDefinition {
  id: string;
  label: string;
  position: [number, number, number];
  labelHeight: number;
  color: number;
}

interface District {
  definition: DistrictDefinition;
  group: THREE.Group;
  emissiveMaterials: THREE.MeshStandardMaterial[];
}

const DISTRICTS: DistrictDefinition[] = [
  {
    id: "client",
    label: "SQL CLIENT",
    position: [-19, 0, 8],
    labelHeight: 3.6,
    color: PALETTE.blue,
  },
  {
    id: "server",
    label: "SERVER PROCESS",
    position: [-13.5, 0, 4.5],
    labelHeight: 4.6,
    color: PALETTE.yellow,
  },
  {
    id: "parser",
    label: "PARSER TOWER",
    position: [-10, 0, 9],
    labelHeight: 5.4,
    color: PALETTE.blue,
  },
  {
    id: "library-cache",
    label: "LIBRARY CACHE PALACE",
    position: [-3.5, 0, 8],
    labelHeight: 6.3,
    color: PALETTE.yellow,
  },
  {
    id: "mutex-gate",
    label: "KGL MUTEX GATE",
    position: [-3.5, 0, 2.7],
    labelHeight: 4.2,
    color: PALETTE.yellow,
  },
  {
    id: "transformer",
    label: "QUERY TRANSFORMER",
    position: [3, 0, 8.8],
    labelHeight: 5.3,
    color: PALETTE.blue,
  },
  {
    id: "optimizer",
    label: "CBO FOUNDRY",
    position: [8.3, 0, 8],
    labelHeight: 5.1,
    color: PALETTE.yellow,
  },
  {
    id: "plan",
    label: "EXECUTION PLAN",
    position: [14, 0, 7.4],
    labelHeight: 5.4,
    color: PALETTE.blue,
  },
  {
    id: "index",
    label: "INDEX RANGE SCAN",
    position: [10.5, 0, 0.8],
    labelHeight: 4.4,
    color: PALETTE.blue,
  },
  {
    id: "table",
    label: "TABLE FULL SCAN",
    position: [16.6, 0, 0],
    labelHeight: 4.1,
    color: PALETTE.blue,
  },
  {
    id: "io-queue",
    label: "I/O QUEUES",
    position: [13.5, 0, -6.3],
    labelHeight: 3.8,
    color: PALETTE.yellow,
  },
  {
    id: "storage",
    label: "DATAFILES",
    position: [19, 0, -9],
    labelHeight: 4.7,
    color: PALETTE.blue,
  },
  {
    id: "buffer-cache",
    label: "BUFFER CACHE",
    position: [3.7, 0, -7.1],
    labelHeight: 3.5,
    color: PALETTE.blue,
  },
  {
    id: "hash-chains",
    label: "BUFFER HASH CHAINS",
    position: [-4.7, 0, -6.5],
    labelHeight: 4.7,
    color: PALETTE.blue,
  },
  {
    id: "join",
    label: "HASH JOIN ARENA",
    position: [6.5, 0, 1.5],
    labelHeight: 4.7,
    color: PALETTE.yellow,
  },
];

const material = (
  color: number,
  emissive = color,
  intensity = 0.22,
  options: Partial<THREE.MeshStandardMaterialParameters> = {},
): THREE.MeshStandardMaterial =>
  new THREE.MeshStandardMaterial({
    color,
    emissive,
    emissiveIntensity: intensity,
    metalness: 0.16,
    roughness: 0.65,
    ...options,
  });

const box = (
  size: [number, number, number],
  meshMaterial: THREE.Material,
  position: [number, number, number] = [0, 0, 0],
): THREE.Mesh => {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), meshMaterial);
  mesh.position.set(...position);
  return mesh;
};

export class CityWorld {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(55, 1, 0.1, 150);

  private readonly controls: OrbitControls;
  private readonly composer: EffectComposer;
  private readonly bloomPass: UnrealBloomPass;
  private readonly renderPass: RenderPass;
  private readonly interior: OptimizerInterior;
  private readonly interiorLabels: HTMLElement;
  private interiorOptions: ExhibitOptions = { ...DEFAULT_EXHIBIT_OPTIONS };
  private interiorMode: "auto" | "inside" | "outside" = "auto";
  private interiorKey = "";
  private currentExhibit: OreExhibit | null = null;
  private interiorStep: FlowStep | null = null;
  private interiorStepProgress = 0;
  private interiorActive = false;
  private interiorStateKey = "";
  private interiorListener: ((state: InteriorState) => void) | null = null;
  private interiorFocusListener: ((text: string) => void) | null = null;
  private readonly districts = new Map<string, District>();
  private readonly labels = new Map<string, HTMLDivElement>();
  private readonly cursorChildren: THREE.MeshStandardMaterial[] = [];
  private cursorScanner: THREE.Mesh | null = null;
  private readonly planNodes: THREE.MeshStandardMaterial[] = [];
  private readonly bufferBlocks: THREE.MeshStandardMaterial[] = [];
  private readonly latchGates: THREE.Group[] = [];
  private readonly animatedRings: THREE.Object3D[] = [];
  private readonly skylineGroups: THREE.Group[] = [];
  private readonly clock = new THREE.Clock();
  private readonly tourCameraPosition = new THREE.Vector3(27, 25, 31);
  private readonly tourCameraTarget = new THREE.Vector3(0, 0.7, 0);
  private readonly smoothedCameraTarget = new THREE.Vector3(0, 0.7, 0);
  private activeDistrict = "";
  private activeAction = "";
  private tourEnabled = true;
  private immersiveView = false;
  private frameHandle = 0;
  private motionPaused = false;
  private readonly reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  private readonly resizeObserver: ResizeObserver;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly labelLayer: HTMLElement,
  ) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.65));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.95;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scene.background = new THREE.Color(PALETTE.background);
    this.scene.fog = new THREE.FogExp2(PALETTE.background, 0.005);
    this.interiorLabels = document.createElement("div");
    this.interiorLabels.className = "interior-labels";
    this.interiorLabels.hidden = true;
    canvas.parentElement!.append(this.interiorLabels);
    this.interior = new OptimizerInterior(this.interiorLabels, (text) =>
      this.interiorFocusListener?.(text),
    );

    this.camera.position.set(27, 25, 31);
    this.camera.lookAt(0, 0, 0);

    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.065;
    this.controls.minDistance = 16;
    this.controls.maxDistance = 64;
    this.controls.maxPolarAngle = Math.PI * 0.49;
    this.controls.target.set(0, 0.7, 0);

    this.composer = new EffectComposer(this.renderer);
    this.renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(this.renderPass);
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(1, 1),
      0.12,
      0.28,
      0.88,
    );
    this.composer.addPass(this.bloomPass);

    this.buildLights();
    this.buildGround();
    this.buildMetropolitanFabric();
    this.buildHighways();
    this.buildDistricts();
    this.resize();
    window.addEventListener("resize", this.resize);
    this.resizeObserver = new ResizeObserver(this.resize);
    this.resizeObserver.observe(canvas);
  }

  start(onFrame: (deltaMs: number) => void): void {
    const animate = () => {
      this.frameHandle = requestAnimationFrame(animate);
      const delta = Math.min(0.05, this.clock.getDelta());
      onFrame(delta * 1000);
      this.controls.update();
      this.animateCity(delta);
      this.updateLabels();
      if (this.interiorActive)
        this.interior.update(
          interiorProgress(
            this.interiorStepProgress,
            this.interiorMode === "inside",
          ),
          this.reducedMotion,
        );
      this.composer.render();
    };
    animate();
  }

  stop(): void {
    cancelAnimationFrame(this.frameHandle);
    window.removeEventListener("resize", this.resize);
    this.controls.dispose();
    this.resizeObserver.disconnect();
    this.interior.dispose();
    this.composer.dispose();
    this.renderer.dispose();
  }

  setActiveStep(step: FlowStep, progress = 0): void {
    this.activeAction = step.action;
    this.setActiveDistrict(step.district);
    this.updateCursorState(step.id, progress);
    this.updatePlanState(step.id);
    this.updateBufferState(step.id);
  }

  setMotionPaused(paused: boolean): void {
    this.motionPaused = paused;
  }

  setTourEnabled(enabled: boolean): void {
    this.tourEnabled = enabled;
    this.controls.enabled = !enabled;
    // Open the camera-facing edge of the model during guided shots. Keep all
    // remaining façades opaque; free exploration restores the complete skyline.
    for (const tower of this.skylineGroups) {
      tower.visible =
        !enabled || (tower.position.z <= 10 && tower.position.x <= 21);
    }
    if (!enabled) {
      this.controls.target.copy(this.smoothedCameraTarget);
    }
    this.refreshInterior();
  }

  bindInterior(
    listener: (state: InteriorState) => void,
    focus: (text: string) => void,
  ): void {
    this.interiorListener = listener;
    this.interiorFocusListener = focus;
    this.interiorStateKey = "";
    this.refreshInterior();
  }

  setInteriorMode(mode: "inside" | "outside"): void {
    this.interiorMode = mode;
    if (mode === "inside") this.setTourEnabled(true);
    this.refreshInterior();
  }

  setExhibitOptions(options: ExhibitOptions): void {
    this.interiorOptions = { ...options };
    this.refreshInterior();
  }

  updateInterior(step: FlowStep, progress: number): void {
    if (step.id !== this.interiorStep?.id)
      this.interiorMode =
        this.interiorActive && step.district === this.interiorStep?.district
          ? "inside"
          : "auto";
    this.interiorStep = step;
    this.interiorStepProgress = progress;
    this.refreshInterior();
  }

  private refreshInterior(): void {
    const key = `${this.interiorStep?.id}:${JSON.stringify(this.interiorOptions)}`;
    if (key !== this.interiorKey) {
      this.interiorKey = key;
      this.currentExhibit = this.interiorStep
        ? oreExhibit(this.interiorStep.id, this.interiorOptions)
        : null;
      if (this.currentExhibit) this.interior.setExhibit(this.currentExhibit);
    }
    const exhibit = this.currentExhibit;
    this.interiorActive = shouldEnterInterior(
      !!exhibit,
      this.tourEnabled,
      this.interiorMode,
      this.interiorStepProgress,
    );
    this.renderPass.scene = this.interiorActive
      ? this.interior.scene
      : this.scene;
    this.renderPass.camera = this.interiorActive
      ? this.interior.camera
      : this.camera;
    this.interiorLabels.hidden = !this.interiorActive;
    this.labelLayer.hidden = this.interiorActive;
    const state: InteriorState = {
      available: !!exhibit,
      active: this.interiorActive,
      room: exhibit?.room ?? "",
      boundary: exhibit?.boundary ?? "",
    };
    const stateKey = JSON.stringify(state);
    if (stateKey !== this.interiorStateKey) {
      this.interiorStateKey = stateKey;
      this.interiorListener?.(state);
    }
  }

  isTourEnabled(): boolean {
    return this.tourEnabled;
  }

  setImmersiveView(enabled: boolean): void {
    this.immersiveView = enabled;
    this.resize();
  }

  followJourney(
    position: THREE.Vector3,
    _tangent: THREE.Vector3,
    step: FlowStep,
    progress: number,
  ): void {
    if (!this.tourEnabled) {
      return;
    }
    const focus = this.districtPosition(step.district);
    const shot = this.shotForStep(step, progress)!;
    const settle = this.reducedMotion
      ? 1
      : THREE.MathUtils.smoothstep(progress, 0.12, 0.35);
    const anchor = position.clone().lerp(focus, settle);
    this.tourCameraPosition.copy(anchor).add(shot.offset);
    this.tourCameraTarget.copy(anchor).add(shot.target);
    // Reserve the left half of the wide scene for the narrative, without moving the world.
    if (!this.immersiveView && this.camera.aspect > 2) {
      const right = new THREE.Vector3(
        shot.offset.z,
        0,
        -shot.offset.x,
      ).normalize();
      this.tourCameraTarget.addScaledVector(right, -8.5);
    }
  }

  resetTourCamera(): void {
    this.tourCameraPosition.set(27, 25, 31);
    this.tourCameraTarget.set(0, 0.7, 0);
    this.activeDistrict = "";
    this.activeAction = "";
    for (const label of this.labels.values()) {
      label.classList.remove("active");
    }
  }

  setActiveDistrict(id: string): void {
    if (id === this.activeDistrict) {
      return;
    }
    this.activeDistrict = id;
    for (const [districtId, district] of this.districts) {
      const active = districtId === id;
      for (const emissive of district.emissiveMaterials) {
        emissive.emissiveIntensity = active ? 0.42 : 0.12;
      }
      this.labels.get(districtId)?.classList.toggle("active", active);
    }
  }

  districtPosition(id: string): THREE.Vector3 {
    const district = this.districts.get(id);
    if (!district) {
      return new THREE.Vector3();
    }
    return district.group.position.clone().add(new THREE.Vector3(0, 2.1, 0));
  }

  add(object: THREE.Object3D): void {
    this.scene.add(object);
  }

  private readonly resize = (): void => {
    const { clientWidth, clientHeight } = this.canvas;
    if (clientWidth === 0 || clientHeight === 0) {
      return;
    }
    this.camera.aspect = clientWidth / clientHeight;
    this.camera.updateProjectionMatrix();
    this.interior.resize(clientWidth, clientHeight);
    this.renderer.setSize(clientWidth, clientHeight, false);
    this.composer.setSize(clientWidth, clientHeight);
    this.bloomPass.resolution.set(clientWidth, clientHeight);
  };

  private buildLights(): void {
    this.scene.add(new THREE.HemisphereLight(0xeaf5ff, 0xbeb49b, 1.8));
    const key = new THREE.DirectionalLight(0xfff0d2, 2.4);
    key.position.set(-12, 24, 12);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    this.scene.add(key);
    const blue = new THREE.PointLight(0xe1f4ff, 18, 28, 2);
    blue.position.set(4, 9, -4);
    this.scene.add(blue);
    const red = new THREE.PointLight(0xffe6c2, 18, 24, 2);
    red.position.set(15, 7, -6);
    this.scene.add(red);
    const yellow = new THREE.PointLight(0xffe6c2, 18, 22, 2);
    yellow.position.set(-4, 8, 5);
    this.scene.add(yellow);
  }

  private buildGround(): void {
    const groundMaterial = new THREE.MeshStandardMaterial({
      color: PALETTE.ground,
      metalness: 0.25,
      roughness: 0.78,
    });
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(48, 32),
      groundMaterial,
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.08;
    ground.receiveShadow = true;
    this.scene.add(ground);

    const grid = new THREE.GridHelper(48, 48, 0xa7bbbe, 0xbacbca);
    grid.position.y = -0.02;
    const gridMaterials = Array.isArray(grid.material)
      ? grid.material
      : [grid.material];
    for (const gridMaterial of gridMaterials) {
      gridMaterial.transparent = true;
      gridMaterial.opacity = 0.18;
    }
    this.scene.add(grid);

    const ringMaterial = new THREE.MeshBasicMaterial({
      color: PALETTE.blue,
      transparent: true,
      opacity: 0.14,
      side: THREE.DoubleSide,
    });
    for (const radius of [8, 14, 21]) {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(radius - 0.04, radius + 0.04, 128),
        ringMaterial,
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 0.01;
      this.scene.add(ring);
    }
  }

  private buildMetropolitanFabric(): void {
    const asphalt = material(0x78949f, 0x000000, 0, {
      metalness: 0.18,
      roughness: 0.9,
    });
    const roadLines = new THREE.MeshBasicMaterial({
      color: 0xece2be,
      transparent: true,
      opacity: 0.72,
    });

    for (const z of [-12.2, -3.1, 4.9, 12.2]) {
      const road = box([47, 0.05, 1.3], asphalt, [0, 0.015, z]);
      this.scene.add(road);
      for (const x of [-20, -12, -4, 4, 12, 20]) {
        this.scene.add(box([2.8, 0.025, 0.055], roadLines, [x, 0.06, z]));
      }
    }
    for (const x of [-21.4, -12.1, -6.9, 0.2, 7.3, 12.8, 21.6]) {
      const road = box([1.25, 0.05, 31], asphalt, [x, 0.016, 0]);
      this.scene.add(road);
      for (const z of [-10, -5, 0, 5, 10]) {
        this.scene.add(box([0.055, 0.025, 2.3], roadLines, [x, 0.06, z]));
      }
    }

    const towers: Array<[number, number, number, number, number]> = [
      [-21, -13.5, 3.1, 8.6, 2.8],
      [-16.8, -13.4, 2.5, 12.5, 2.7],
      [-12.8, -13.4, 3.2, 7.4, 2.8],
      [-8.5, -13.3, 2.6, 10.8, 2.6],
      [-3.8, -13.2, 3.4, 14.5, 2.9],
      [1, -13.3, 2.8, 9.3, 2.7],
      [6, -13.5, 3.5, 13.2, 2.8],
      [10.6, -13.4, 2.8, 8.1, 2.7],
      [15.2, -13.3, 3.1, 11.4, 2.8],
      [20.3, -13.4, 3.2, 15.2, 2.8],
      [-21.5, 13.4, 3.2, 11.5, 2.8],
      [-16.6, 13.3, 2.6, 7.8, 2.5],
      [-12.2, 13.4, 3.1, 14.2, 2.8],
      [-7.4, 13.3, 2.7, 9.4, 2.6],
      [-2.5, 13.4, 3.2, 12.1, 2.8],
      [2.4, 13.3, 2.6, 8.5, 2.6],
      [7.1, 13.4, 3.3, 15.8, 2.9],
      [12.1, 13.3, 2.7, 10.2, 2.5],
      [16.8, 13.4, 3.2, 13.7, 2.8],
      [21.1, 13.2, 2.7, 8.8, 2.6],
      [-22.6, -7.8, 2.5, 9.2, 3.1],
      [-22.7, -1.5, 2.6, 13.4, 3.2],
      [-22.7, 5.2, 2.7, 8.7, 3],
      [22.8, -3.2, 2.7, 10.8, 3.2],
      [22.7, 4.2, 2.5, 14.4, 3.1],
      [22.5, 9.2, 2.6, 8.1, 3],
      [-15.4, -7.6, 2.6, 7.6, 2.5],
      [-9.3, -7.8, 2.5, 10.4, 2.4],
      [-9.2, -1.7, 2.4, 6.8, 2.4],
      [0.2, -1.9, 2.4, 9.2, 2.4],
    ];
    towers.forEach(([x, z, width, height, depth], index) => {
      this.buildSkyscraper(
        x,
        z,
        width,
        z > 10 ? height * 0.38 : height * 0.75,
        depth,
        index,
      );
    });

    const lampMaterial = new THREE.MeshBasicMaterial({ color: PALETTE.yellow });
    for (let index = 0; index < 30; index += 1) {
      const x = -21 + (index % 10) * 4.65;
      const z = index < 10 ? -11.3 : index < 20 ? 11.3 : -2.35;
      const pole = box([0.06, 1.25, 0.06], material(0x151b24, 0x000000, 0), [
        x,
        0.62,
        z,
      ]);
      const light = box([0.12, 0.08, 0.12], lampMaterial, [x, 1.28, z]);
      this.scene.add(pole, light);
    }
  }

  private buildSkyscraper(
    x: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    index: number,
  ): void {
    const group = new THREE.Group();
    group.position.set(x, 0, z);
    const accent =
      index % 5 === 0
        ? PALETTE.yellow
        : index % 3 === 0
          ? PALETTE.teal
          : PALETTE.blue;
    const glass = material(
      index % 3 === 0 ? 0xc6d5c8 : 0x86b9ce,
      accent,
      0.025,
      {
        metalness: 0.18,
        roughness: 0.42,
      },
    );
    const podium = box(
      [width + 0.5, 0.5, depth + 0.5],
      material(0xeee2cb, 0x000000, 0),
      [0, 0.25, 0],
    );
    group.add(podium);
    const round = index % 5 === 2;
    if (round) {
      const drum = new THREE.Mesh(
        new THREE.CylinderGeometry(width * 0.5, width * 0.5, height, 12),
        glass,
      );
      drum.scale.z = depth / width;
      drum.position.y = height / 2 + 0.5;
      group.add(drum);
    } else
      group.add(box([width, height, depth], glass, [0, height / 2 + 0.5, 0]));
    const crownHeight = Math.min(2.2, height * 0.18);
    group.add(
      box([width * 0.68, crownHeight, depth * 0.72], glass, [
        0,
        height + crownHeight / 2 + 0.5,
        0,
      ]),
    );

    const windowMaterial = new THREE.MeshBasicMaterial({
      color: index % 4 === 0 ? 0xcab17b : 0x487fa2,
    });
    const floors = Math.max(3, Math.floor(height / 1.35));
    for (let floor = 0; floor < floors; floor += 1) {
      const y = 1.1 + floor * ((height - 0.7) / floors);
      if (round) {
        const belt = new THREE.Mesh(
          new THREE.CylinderGeometry(width * 0.51, width * 0.51, 0.12, 12),
          material(0xe9ddc5, 0x000000, 0),
        );
        belt.scale.z = depth / width;
        belt.position.y = y;
        group.add(belt);
        continue;
      }
      group.add(
        box([width * 0.76, 0.55, 0.025], windowMaterial, [
          0,
          y,
          depth / 2 + 0.015,
        ]),
      );
      group.add(
        box([0.025, 0.55, depth * 0.72], windowMaterial, [
          width / 2 + 0.015,
          y,
          0,
        ]),
      );
    }
    const limestone = material(0xf3e7d1, 0x000000, 0);
    group.add(
      box([width + 0.2, 0.18, depth + 0.2], limestone, [0, height + 0.5, 0]),
    );
    group.add(
      box([0.8, 1.1, 0.08], material(0x365d70, 0x000000, 0), [
        0,
        1,
        depth / 2 + 0.06,
      ]),
    );
    group.add(box([1.3, 0.14, 0.8], limestone, [0, 1.6, depth / 2 + 0.35]));
    for (const side of [-1, 1]) {
      const tree = new THREE.Mesh(
        new THREE.IcosahedronGeometry(0.55, 1),
        material(0x75a38a, 0x000000, 0),
      );
      tree.position.set(side * width * 0.38, 0.95, depth / 2 + 0.5);
      group.add(tree);
    }
    if (index % 4 === 0) {
      group.add(
        box([0.05, 2.1, 0.05], windowMaterial, [
          0,
          height + crownHeight + 1.5,
          0,
        ]),
      );
    }
    this.scene.add(group);
    this.skylineGroups.push(group);
  }

  private buildHighways(): void {
    const routes: Array<{
      color: number;
      points: Array<[number, number, number]>;
    }> = [
      {
        color: PALETTE.blue,
        points: [
          [-19, 0.18, 8],
          [-13.5, 0.18, 4.5],
          [-10, 0.18, 9],
          [-3.5, 0.18, 8],
          [3, 0.18, 8.8],
          [8.3, 0.18, 8],
          [14, 0.18, 7.4],
        ],
      },
      {
        color: PALETTE.yellow,
        points: [
          [14, 0.2, 7.4],
          [10.5, 0.2, 0.8],
          [6.5, 0.2, 1.5],
          [16.6, 0.2, 0],
        ],
      },
      {
        color: PALETTE.teal,
        points: [
          [10.5, 0.15, 0.8],
          [13.5, 0.15, -6.3],
          [19, 0.15, -9],
          [3.7, 0.15, -7.1],
          [-4.7, 0.15, -6.5],
        ],
      },
      {
        color: PALETTE.blue,
        points: [
          [3.7, 0.13, -7.1],
          [6.5, 0.13, 1.5],
          [-13.5, 0.13, 4.5],
          [-19, 0.13, 8],
        ],
      },
    ];

    for (const route of routes) {
      const curve = new THREE.CatmullRomCurve3(
        route.points.map((point) => new THREE.Vector3(...point)),
        false,
        "catmullrom",
        0.15,
      );
      this.scene.add(this.buildRoadRibbon(curve, 0.92));
      const lane = new THREE.Mesh(
        new THREE.TubeGeometry(curve, 160, 0.034, 6, false),
        new THREE.MeshBasicMaterial({ color: route.color }),
      );
      lane.position.y = 0.11;
      this.scene.add(lane);
    }
  }

  private buildRoadRibbon(
    curve: THREE.CatmullRomCurve3,
    width: number,
  ): THREE.Mesh {
    const segments = 160;
    const positions: number[] = [];
    const indices: number[] = [];
    const up = new THREE.Vector3(0, 1, 0);
    for (let index = 0; index <= segments; index += 1) {
      const t = index / segments;
      const point = curve.getPoint(t);
      const tangent = curve.getTangent(t).normalize();
      const right = new THREE.Vector3().crossVectors(tangent, up).normalize();
      const leftPoint = point.clone().addScaledVector(right, -width / 2);
      const rightPoint = point.clone().addScaledVector(right, width / 2);
      positions.push(
        leftPoint.x,
        leftPoint.y,
        leftPoint.z,
        rightPoint.x,
        rightPoint.y,
        rightPoint.z,
      );
      if (index < segments) {
        const base = index * 2;
        indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3);
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(positions, 3),
    );
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return new THREE.Mesh(
      geometry,
      material(0x06080c, 0x000000, 0, {
        metalness: 0.26,
        roughness: 0.78,
        side: THREE.DoubleSide,
      }),
    );
  }

  private buildDistricts(): void {
    for (const definition of DISTRICTS) {
      const group = new THREE.Group();
      group.position.set(...definition.position);
      const emissiveMaterials: THREE.MeshStandardMaterial[] = [];
      this.buildDistrict(
        definition.id,
        group,
        definition.color,
        emissiveMaterials,
      );
      this.scene.add(group);
      this.districts.set(definition.id, {
        definition,
        group,
        emissiveMaterials,
      });

      const label = document.createElement("div");
      label.className = "city-label";
      label.innerHTML = `<i></i>${definition.label}`;
      label.dataset.district = definition.id;
      this.labelLayer.append(label);
      this.labels.set(definition.id, label);
    }
  }

  private buildDistrict(
    id: string,
    group: THREE.Group,
    color: number,
    emissiveMaterials: THREE.MeshStandardMaterial[],
  ): void {
    switch (id) {
      case "client":
        this.buildClient(group, color, emissiveMaterials);
        break;
      case "server":
        this.buildServer(group, color, emissiveMaterials);
        break;
      case "parser":
        this.buildParser(group, color, emissiveMaterials);
        break;
      case "library-cache":
        this.buildLibraryCache(group, color, emissiveMaterials);
        break;
      case "mutex-gate":
        this.buildMutexCastle(group, color, emissiveMaterials);
        break;
      case "transformer":
        this.buildTransformer(group, color, emissiveMaterials);
        break;
      case "optimizer":
        this.buildOptimizer(group, color, emissiveMaterials);
        break;
      case "plan":
        this.buildPlan(group, color, emissiveMaterials);
        break;
      case "index":
        this.buildIndex(group, color, emissiveMaterials);
        break;
      case "table":
        this.buildTable(group, color, emissiveMaterials);
        break;
      case "io-queue":
        this.buildIoQueue(group, color, emissiveMaterials);
        break;
      case "storage":
        this.buildStorage(group, color, emissiveMaterials);
        break;
      case "buffer-cache":
        this.buildBufferCache(group, color, emissiveMaterials);
        break;
      case "hash-chains":
        this.buildHashChains(group, color, emissiveMaterials);
        break;
      case "join":
        this.buildJoin(group, color, emissiveMaterials);
        break;
      default:
        break;
    }
  }

  private cityMaterial(
    color: number,
    list: THREE.MeshStandardMaterial[],
    intensity = 0.22,
  ): THREE.MeshStandardMaterial {
    const value = material(
      intensity > 0.5 ? color : PALETTE.structure,
      color,
      Math.min(intensity, 0.12),
    );
    list.push(value);
    return value;
  }

  private buildClient(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    const shell = this.cityMaterial(color, mats);
    group.add(box([3.3, 0.28, 2.6], shell, [0, 0.14, 0]));
    const screen = this.cityMaterial(color, mats, 0.8);
    group.add(box([2.6, 1.7, 0.22], screen, [0, 1.55, -0.4]));
    group.add(box([0.22, 1.1, 0.22], shell, [0, 0.75, 0]));
    group.add(box([1.2, 0.15, 0.7], shell, [0, 0.15, 1]));
  }

  private buildServer(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    const shell = this.cityMaterial(color, mats);
    for (let level = 0; level < 4; level += 1) {
      const width = 3.3 - level * 0.28;
      group.add(box([width, 0.75, 2.8], shell, [0, 0.42 + level * 0.82, 0]));
    }
    const core = this.cityMaterial(PALETTE.red, mats, 0.65);
    const beacon = new THREE.Mesh(new THREE.OctahedronGeometry(0.7, 0), core);
    beacon.position.y = 4.05;
    group.add(beacon);
    this.animatedRings.push(beacon);
  }

  private buildParser(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    const shell = this.cityMaterial(color, mats);
    group.add(box([3.1, 3.7, 2.8], shell, [0, 1.85, 0]));
    group.add(box([2.35, 1.25, 2.2], shell, [0, 4.3, 0]));
    group.add(box([1.4, 0.75, 1.4], shell, [0, 5.3, 0]));
    for (let level = 0; level < 5; level += 1) {
      const windowBand = box(
        [3.16, 0.045, 0.035],
        new THREE.MeshBasicMaterial({ color }),
        [0, 0.65 + level * 0.68, 1.415],
      );
      group.add(windowBand);
    }
    group.add(
      box(
        [0.07, 1.9, 0.07],
        new THREE.MeshBasicMaterial({ color }),
        [0, 6.6, 0],
      ),
    );
  }

  private buildLibraryCache(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    const shell = this.cityMaterial(color, mats);
    group.add(box([6.2, 0.45, 3.8], shell, [0, 0.23, 0]));
    group.add(box([2.45, 4.9, 2.5], shell, [-1.55, 2.68, 0]));
    group.add(box([1.8, 1.25, 1.95], shell, [-1.55, 5.75, 0]));
    group.add(
      box(
        [0.07, 1.5, 0.07],
        new THREE.MeshBasicMaterial({ color }),
        [-1.55, 7.1, 0],
      ),
    );
    for (let floor = 0; floor < 6; floor += 1) {
      group.add(
        box([2.5, 0.04, 0.035], new THREE.MeshBasicMaterial({ color }), [
          -1.55,
          0.9 + floor * 0.7,
          1.265,
        ]),
      );
    }

    for (let child = 0; child < 2; child += 1) {
      const childMaterial = material(
        child === 0 ? PALETTE.blue : 0x15181f,
        child === 0 ? PALETTE.blue : PALETTE.inactive,
        child === 0 ? 0.45 : 0.08,
      );
      mats.push(childMaterial);
      this.cursorChildren.push(childMaterial);
      const pod = box([1.55, 2.75, 1.55], childMaterial, [
        0.65 + child * 2,
        1.6,
        0,
      ]);
      group.add(pod);
      group.add(
        box([1.1, 0.65, 1.1], childMaterial, [0.65 + child * 2, 3.3, 0]),
      );
      group.add(
        box(
          [0.95, 0.04, 0.03],
          new THREE.MeshBasicMaterial({
            color: child === 0 ? PALETTE.blue : PALETTE.yellow,
          }),
          [0.65 + child * 2, 2.05, 0.79],
        ),
      );
    }

    const bridgeMaterial = new THREE.MeshBasicMaterial({ color });
    for (const x of [-0.1, 1.85]) {
      const bridge = box([1.2, 0.08, 0.12], bridgeMaterial, [x, 2.2, 0]);
      group.add(bridge);
    }

    this.cursorScanner = box(
      [0.12, 3.1, 1.9],
      new THREE.MeshBasicMaterial({
        color: PALETTE.blue,
        transparent: true,
        opacity: 0.42,
      }),
      [0.05, 1.6, 0],
    );
    this.cursorScanner.visible = false;
    group.add(this.cursorScanner);
  }

  private buildMutexCastle(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    const shell = this.cityMaterial(color, mats, 0.35);
    group.add(box([5.7, 0.42, 2.7], shell, [0, 0.21, 0]));
    for (const x of [-2, 2]) {
      group.add(box([1.25, 3.6, 1.35], shell, [x, 2, 0]));
      group.add(box([0.9, 0.8, 1], shell, [x, 4.2, 0]));
      group.add(
        box([1.3, 0.045, 0.03], new THREE.MeshBasicMaterial({ color }), [
          x,
          2.4,
          0.69,
        ]),
      );
    }
    const gate = new THREE.Group();
    const gateMaterial = this.cityMaterial(color, mats, 0.8);
    gate.add(box([0.32, 2.6, 0.25], gateMaterial, [-0.65, 1.6, 0]));
    gate.add(box([0.32, 2.6, 0.25], gateMaterial, [0.65, 1.6, 0]));
    gate.add(box([1.62, 0.3, 0.25], gateMaterial, [0, 2.75, 0]));
    group.add(gate);
    this.latchGates.push(gate);
  }

  private buildTransformer(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    const shell = this.cityMaterial(color, mats);
    group.add(box([4.4, 0.5, 3.7], shell, [0, 0.25, 0]));
    const glass = material(0x83b9d3, 0x000000, 0);
    group.add(box([4, 3.6, 0.28], shell, [0, 2.1, -1.6]));
    for (const x of [-1.7, 1.7]) {
      group.add(box([0.4, 3.6, 3.4], shell, [x, 2.1, 0]));
      group.add(box([0.5, 3.8, 0.5], shell, [x, 2.2, 1.6]));
      group.add(box([0.1, 2, 2.2], glass, [x * 1.13, 2.3, 0]));
    }
    group.add(box([4.4, 0.28, 3.8], shell, [0, 4.05, 0]));
    group.add(box([2.4, 0.15, 2.6], glass, [0, 4.25, 0]));
    group.add(box([2.6, 0.25, 0.7], material(PALETTE.yellow), [0, 3.25, 1.8]));
    group.add(box([1.2, 2.3, 0.15], glass, [0, 1.7, -1.4]));
  }

  private buildOptimizer(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    const shell = this.cityMaterial(color, mats);
    group.add(box([4.3, 3.7, 3.3], shell, [0, 1.85, 0]));
    for (let row = 0; row < 3; row += 1) {
      for (let col = 0; col < 4; col += 1) {
        const windowMaterial = this.cityMaterial(
          (row + col) % 2 === 0 ? color : PALETTE.blue,
          mats,
          0.7,
        );
        group.add(
          box([0.48, 0.4, 0.08], windowMaterial, [
            -1.35 + col * 0.9,
            0.8 + row * 0.9,
            1.69,
          ]),
        );
      }
    }
    const chimney = new THREE.Mesh(
      new THREE.CylinderGeometry(0.35, 0.5, 1.4, 8),
      shell,
    );
    chimney.position.set(1.25, 4.35, 0);
    group.add(chimney);
  }

  private buildPlan(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    const positions: Array<[number, number, number]> = [
      [0, 4.2, 0],
      [0, 3.05, 0],
      [0, 1.9, 0],
      [-1.4, 0.65, 0],
      [1.4, 0.65, 0],
    ];
    const nodeMaterial = positions.map((_, index) => {
      const value = material(
        PALETTE.black,
        index === 4 ? PALETTE.red : color,
        0.15,
      );
      mats.push(value);
      this.planNodes.push(value);
      return value;
    });
    positions.forEach((position, index) => {
      group.add(
        box(
          [index < 3 ? 2.5 : 2.1, 0.55, 1.35],
          nodeMaterial[index]!,
          position,
        ),
      );
    });
    const lineMaterial = new THREE.LineBasicMaterial({ color: PALETTE.yellow });
    const links: Array<[number, number]> = [
      [0, 1],
      [1, 2],
      [2, 3],
      [2, 4],
    ];
    for (const [from, to] of links) {
      const line = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(...positions[from]!),
          new THREE.Vector3(...positions[to]!),
        ]),
        lineMaterial,
      );
      group.add(line);
    }
  }

  private buildIndex(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    const shell = this.cityMaterial(color, mats);
    for (let level = 0; level < 4; level += 1) {
      const count = level + 1;
      for (let node = 0; node < count; node += 1) {
        const x = (node - (count - 1) / 2) * (2.5 / count + 0.45);
        group.add(box([1.1, 0.5, 1.05], shell, [x, 0.45 + level * 0.95, 0]));
      }
    }
    const scanRail = box(
      [4.6, 0.1, 0.16],
      new THREE.MeshBasicMaterial({ color }),
      [0, 0.12, 0.75],
    );
    group.add(scanRail);
  }

  private buildTable(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    for (let row = 0; row < 6; row += 1) {
      const rowMaterial = this.cityMaterial(
        row < 2 ? color : PALETTE.yellow,
        mats,
        row < 2 ? 0.5 : 0.16,
      );
      const slab = box([4.8, 0.42, 2.8], rowMaterial, [0, 0.3 + row * 0.58, 0]);
      group.add(slab);
    }
    const scanBeam = box(
      [5.2, 0.09, 3.2],
      new THREE.MeshBasicMaterial({ color }),
      [0, 3.75, 0],
    );
    group.add(scanBeam);
  }

  private buildIoQueue(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    const base = this.cityMaterial(color, mats);
    group.add(box([7.2, 0.32, 3.8], base, [0, 0.16, 0]));
    const lanes = [
      { z: -0.9, laneColor: PALETTE.yellow, count: 1 },
      { z: 0.9, laneColor: PALETTE.red, count: 4 },
    ];
    for (const lane of lanes) {
      group.add(
        box(
          [6.6, 0.08, 0.12],
          new THREE.MeshBasicMaterial({ color: lane.laneColor }),
          [0, 0.42, lane.z],
        ),
      );
      for (let index = 0; index < lane.count; index += 1) {
        const request = box(
          [0.62, 0.62, 0.62],
          this.cityMaterial(lane.laneColor, mats, 0.72),
          [-2.35 + index * 0.88, 0.78, lane.z],
        );
        group.add(request);
      }
    }
    for (const x of [-3.15, 3.15]) {
      group.add(box([0.75, 1.65, 1.25], base, [x, 1.05, 0]));
      group.add(
        box([0.8, 0.05, 0.05], new THREE.MeshBasicMaterial({ color }), [
          x,
          1.4,
          0.64,
        ]),
      );
    }
    group.add(box([6.55, 0.22, 0.34], base, [0, 2.45, 0]));
    for (const x of [-2.65, 2.65]) {
      group.add(box([0.18, 2.15, 0.25], base, [x, 1.45, 0]));
    }
  }

  private buildStorage(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    for (let file = 0; file < 3; file += 1) {
      const fileGroup = new THREE.Group();
      const diskMaterial = this.cityMaterial(
        file === 1 ? PALETTE.yellow : color,
        mats,
        0.35,
      );
      for (let platter = 0; platter < 7; platter += 1) {
        const disk = new THREE.Mesh(
          new THREE.CylinderGeometry(1.2, 1.2, 0.34, 32),
          diskMaterial,
        );
        disk.position.y = 0.25 + platter * 0.45;
        fileGroup.add(disk);
      }
      fileGroup.position.x = (file - 1) * 2.8;
      group.add(fileGroup);
    }
  }

  private buildBufferCache(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    const base = this.cityMaterial(color, mats);
    group.add(box([10.8, 0.28, 6.8], base, [0, 0.14, 0]));
    for (let z = 0; z < 7; z += 1) {
      for (let x = 0; x < 12; x += 1) {
        const blockMaterial = material(
          PALETTE.black,
          (x + z) % 7 === 0 ? PALETTE.yellow : color,
          0.08 + ((x * 3 + z * 5) % 5) * 0.035,
        );
        mats.push(blockMaterial);
        this.bufferBlocks.push(blockMaterial);
        const height = 0.34 + ((x * 5 + z * 7) % 4) * 0.11;
        group.add(
          box([0.7, height, 0.68], blockMaterial, [
            (x - 5.5) * 0.82,
            0.36 + height / 2,
            (z - 3) * 0.84,
          ]),
        );
      }
    }
  }

  private buildHashChains(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    const base = this.cityMaterial(color, mats);
    group.add(box([7.6, 0.35, 5.3], base, [0, 0.18, 0]));
    for (let bucket = 0; bucket < 5; bucket += 1) {
      const bucketColor = bucket === 2 ? PALETTE.yellow : color;
      const bucketMaterial = this.cityMaterial(
        bucketColor,
        mats,
        bucket === 2 ? 0.72 : 0.22,
      );
      const x = (bucket - 2) * 1.35;
      const tower = new THREE.Mesh(
        new THREE.CylinderGeometry(0.42, 0.58, 3.2, 8),
        bucketMaterial,
      );
      tower.position.set(x, 1.85, -1.2);
      group.add(tower);
      for (let link = 0; link < 3; link += 1) {
        const header = new THREE.Mesh(
          new THREE.SphereGeometry(0.28, 12, 12),
          bucketMaterial,
        );
        header.position.set(x, 0.78 + link * 0.75, 0.35 + link * 0.46);
        group.add(header);
        if (link > 0) {
          const chain = box(
            [0.08, 0.52, 0.08],
            new THREE.MeshBasicMaterial({ color: bucketColor }),
            [x, 0.65 + link * 0.75, 0.12 + link * 0.46],
          );
          chain.rotation.x = -0.5;
          group.add(chain);
        }
      }
    }
    const gate = new THREE.Group();
    const gateMaterial = this.cityMaterial(color, mats, 0.75);
    gate.add(box([0.35, 3, 0.35], gateMaterial, [-3.2, 1.65, 1.45]));
    gate.add(box([0.35, 3, 0.35], gateMaterial, [-1.9, 1.65, 1.45]));
    gate.add(box([1.65, 0.3, 0.35], gateMaterial, [-2.55, 3, 1.45]));
    group.add(gate);
    this.latchGates.push(gate);
  }

  private buildJoin(
    group: THREE.Group,
    color: number,
    mats: THREE.MeshStandardMaterial[],
  ): void {
    const shell = this.cityMaterial(color, mats);
    const lower = new THREE.Mesh(
      new THREE.CylinderGeometry(2.4, 2.8, 1.2, 16),
      shell,
    );
    lower.position.y = 0.65;
    group.add(lower);
    const arena = new THREE.Mesh(
      new THREE.TorusGeometry(2.2, 0.34, 12, 48),
      this.cityMaterial(color, mats, 0.72),
    );
    arena.rotation.x = Math.PI / 2;
    arena.position.y = 2;
    group.add(arena);
    const core = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.2, 1),
      this.cityMaterial(PALETTE.blue, mats, 0.6),
    );
    core.position.y = 2.15;
    group.add(core);
    this.animatedRings.push(arena, core);
  }

  private updateCursorState(stepId: string, progress: number): void {
    const reachedScan = [
      "child-zero-scan",
      "child-zero-reject",
      "child-one-create",
    ].includes(stepId);
    const rejected =
      stepId === "child-zero-reject" || stepId === "child-one-create";
    const created = stepId === "child-one-create";
    const childZero = this.cursorChildren[0];
    const childOne = this.cursorChildren[1];
    if (childZero) {
      childZero.emissive.setHex(rejected ? PALETTE.red : PALETTE.blue);
      childZero.emissiveIntensity = reachedScan ? 1.15 : 0.28;
    }
    if (childOne) {
      childOne.emissive.setHex(PALETTE.yellow);
      childOne.color.setHex(created ? PALETTE.yellow : 0x15181f);
      childOne.emissiveIntensity = created ? 1.25 : 0.06;
    }
    if (this.cursorScanner) {
      this.cursorScanner.visible = reachedScan;
      if (stepId === "child-zero-scan") {
        this.cursorScanner.position.x = 0.05 + progress * 1.2;
        const scannerMaterial = this.cursorScanner
          .material as THREE.MeshBasicMaterial;
        scannerMaterial.color.setHex(PALETTE.blue);
        scannerMaterial.opacity =
          0.35 + Math.sin(progress * Math.PI * 6) * 0.12;
      } else if (rejected) {
        this.cursorScanner.position.x = 0.65;
        const scannerMaterial = this.cursorScanner
          .material as THREE.MeshBasicMaterial;
        scannerMaterial.color.setHex(
          stepId === "child-one-create" ? PALETTE.yellow : PALETTE.red,
        );
        scannerMaterial.opacity = 0.55;
      }
    }
  }

  private updatePlanState(stepId: string): void {
    const planOrder = ["plan-root", "plan-join", "plan-index", "plan-full"];
    const activeIndex = planOrder.indexOf(stepId);
    this.planNodes.forEach((node, index) => {
      const lit = activeIndex >= 0 && index <= Math.min(4, activeIndex + 1);
      node.emissiveIntensity = lit ? 1.2 : 0.12;
    });
  }

  private updateBufferState(stepId: string): void {
    const single = [
      "single-block-flight",
      "buffer-hash",
      "cbc-latch",
      "buffer-link",
    ].includes(stepId);
    const multi = stepId === "multi-block-flight";
    this.bufferBlocks.forEach((blockMaterial, index) => {
      if (
        (single && index === 39) ||
        (multi && [18, 31, 44, 57].includes(index))
      ) {
        blockMaterial.emissive.setHex(multi ? PALETTE.red : PALETTE.yellow);
        blockMaterial.emissiveIntensity = 1.35;
      } else {
        blockMaterial.emissiveIntensity = 0.11;
      }
    });
  }

  private animateCity(delta: number): void {
    const motionDelta = this.motionPaused || this.reducedMotion ? 0 : delta;
    const time = performance.now() * 0.001;
    this.animatedRings.forEach((object, index) => {
      object.rotation.y += motionDelta * (0.12 + index * 0.025);
      if (object.type === "Mesh" && motionDelta > 0) {
        object.position.y += Math.sin(time * 1.4 + index) * 0.0008;
      }
    });
    if (
      !this.motionPaused &&
      !this.reducedMotion &&
      (this.activeAction === "mutex-acquire" ||
        this.activeAction === "latch-acquire")
    ) {
      this.latchGates.forEach((gate, index) => {
        gate.rotation.y = Math.sin(time * 5 + index) * 0.08;
      });
    } else {
      this.latchGates.forEach((gate) => {
        gate.rotation.y *= 0.9;
      });
    }

    const district = this.districts.get(this.activeDistrict);
    if (district) {
      const pulse = 0.24;
      for (const activeMaterial of district.emissiveMaterials) {
        activeMaterial.emissiveIntensity = Math.max(
          activeMaterial.emissiveIntensity,
          pulse,
        );
      }
    }

    if (this.tourEnabled) {
      const cameraEase = this.reducedMotion ? 1 : 1 - Math.exp(-delta * 1.7);
      const targetEase = this.reducedMotion ? 1 : 1 - Math.exp(-delta * 2.1);
      this.camera.position.lerp(this.tourCameraPosition, cameraEase);
      this.smoothedCameraTarget.lerp(this.tourCameraTarget, targetEase);
      this.camera.lookAt(this.smoothedCameraTarget);
    }
  }

  private shotForStep(
    step: FlowStep,
    progress: number,
  ): { offset: THREE.Vector3; target: THREE.Vector3 } | null {
    const breathe = Math.sin(progress * Math.PI) * 0.35;
    if (
      step.id === "client-request" ||
      step.id === "server-process" ||
      step.id === "parse-entry"
    ) {
      return {
        offset: new THREE.Vector3(12.5, 11.5 + breathe, 14.5),
        target: new THREE.Vector3(0, 1.2, 0),
      };
    }
    if (
      step.action === "mutex-acquire" ||
      step.action === "mutex-release" ||
      step.action === "latch-acquire"
    ) {
      return {
        offset: new THREE.Vector3(11.5 + breathe, 9.5, 13),
        target: new THREE.Vector3(0, 0.9, 0),
      };
    }
    if (
      step.action === "child-scan" ||
      step.action === "child-reject" ||
      step.action === "child-create"
    ) {
      return {
        offset: new THREE.Vector3(11.8, 9.8 + breathe, 13.2),
        target: new THREE.Vector3(0.8, 1.1, 0),
      };
    }
    if (step.action === "plan-node") {
      return {
        offset: new THREE.Vector3(9.8, 8.2 + breathe, 11.4),
        target: new THREE.Vector3(0, 0.65, 0),
      };
    }
    if (step.action === "io-queue" || step.action === "block-flight") {
      return {
        offset: new THREE.Vector3(-12.5, 10.2 + breathe, 14.5),
        target: new THREE.Vector3(0, 0.55, 0),
      };
    }
    if (step.action === "hash-chain") {
      return {
        offset: new THREE.Vector3(11.5, 9.8 + breathe, 13.4),
        target: new THREE.Vector3(0, 0.8, 0),
      };
    }
    if (
      step.phase === "transform" ||
      step.phase === "optimize" ||
      step.action === "row-source" ||
      step.action === "rows-return"
    ) {
      return {
        offset: new THREE.Vector3(12.4, 10.8 + breathe, 14.2),
        target: new THREE.Vector3(0, 0.8, 0),
      };
    }
    return {
      offset: new THREE.Vector3(12.2, 10.5 + breathe, 14),
      target: new THREE.Vector3(0, 0.8, 0),
    };
  }

  private updateLabels(): void {
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    for (const [id, district] of this.districts) {
      const projected = district.group.position
        .clone()
        .add(new THREE.Vector3(0, district.definition.labelHeight, 0))
        .project(this.camera);
      const label = this.labels.get(id);
      if (!label) {
        continue;
      }
      const labelAllowed =
        !this.tourEnabled || !this.activeDistrict || id === this.activeDistrict;
      const clearOfNarrative =
        !this.tourEnabled ||
        this.immersiveView ||
        (this.camera.aspect > 2 && projected.x > 0.22);
      const visible =
        labelAllowed && clearOfNarrative && projected.z > -1 && projected.z < 1;
      label.style.opacity = visible ? "1" : "0";
      label.style.transform = `translate(-50%, -50%) translate(${(projected.x * 0.5 + 0.5) * width}px, ${(-projected.y * 0.5 + 0.5) * height}px)`;
    }
  }
}
